import localforage from 'localforage';
import { STORAGE_KEYS } from '@/api/config';
import { clearAllStores } from '@/utils/storage/indexed-db-storage';
import {
  dropDatabase as dropTargetCacheDB,
} from '@/api/ctwa-ads-management/target-cache.service';
import {
  dropDatabase as dropPropertyCacheDB,
} from '@/api/property-management/property-management-cache.service';

/**
 * Known IndexedDB database names used by the application
 */
const KNOWN_INDEXED_DB_NAMES = [
  'notifyy', // Centralized IndexedDB storage
  'notifyy_target_cache', // Legacy target cache (for cleanup)
  'notifyy_property_management_cache', // Legacy property cache (for cleanup)
  'localforage', // Common library storage
  'keyval-store', // idb-keyval
] as const;

/**
 * Delete a specific IndexedDB database by name
 */
const deleteIndexedDBByName = (dbName: string): Promise<void> => {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(dbName);
      request.onsuccess = () => {
        console.log(`[Auth] IndexedDB "${dbName}" deleted`);
        resolve();
      };
      request.onerror = () => {
        console.warn(`[Auth] Failed to delete IndexedDB "${dbName}":`, request.error);
        resolve(); // Don't reject, continue with other cleanup
      };
      request.onblocked = () => {
        console.warn(`[Auth] IndexedDB "${dbName}" is blocked, attempting to close connections`);
        resolve(); // Don't block the cleanup process
      };
    } catch (error) {
      console.warn(`[Auth] Error deleting IndexedDB "${dbName}":`, error);
      resolve();
    }
  });
};

/**
 * Clear all localforage stores
 */
const clearLocalForageStores = async (): Promise<void> => {
  try {
    // Clear all centralized IndexedDB stores
    await clearAllStores().catch((e) =>
      console.warn('[Auth] Failed to clear centralized stores:', e)
    );

    // Clear default localforage instance
    await localforage.clear().catch((e) =>
      console.warn('[Auth] Failed to clear default localforage:', e)
    );

    console.log('[Auth] LocalForage stores cleared');
  } catch (error) {
    console.warn('[Auth] Error clearing localforage stores:', error);
  }
};

/**
 * Drop all localforage database instances
 */
const dropLocalForageDatabases = async (): Promise<void> => {
  try {
    // Drop target cache databases
    await dropTargetCacheDB().catch((e) =>
      console.warn('[Auth] Failed to drop target cache DB:', e)
    );

    // Drop property cache database
    await dropPropertyCacheDB().catch((e) =>
      console.warn('[Auth] Failed to drop property cache DB:', e)
    );

    // Drop default localforage instance
    await localforage.dropInstance().catch((e) =>
      console.warn('[Auth] Failed to drop default localforage instance:', e)
    );

    console.log('[Auth] LocalForage databases dropped');
  } catch (error) {
    console.warn('[Auth] Error dropping localforage databases:', error);
  }
};

/**
 * Clear all IndexedDB databases
 */
const clearIndexedDB = async (): Promise<void> => {
  try {
    if (!window.indexedDB) return;

    // First, clear all localforage stores (this is the clean way)
    await clearLocalForageStores();

    // Then drop the databases
    await dropLocalForageDatabases();

    // Delete any remaining known database names (fallback for non-localforage DBs)
    await Promise.all(
      KNOWN_INDEXED_DB_NAMES.map((dbName) => deleteIndexedDBByName(dbName))
    );

    // Then try to get all database names if supported (for any unknown databases)
    if ('databases' in indexedDB) {
      try {
        const databases = await indexedDB.databases();
        await Promise.all(
          databases.map((db) => {
            if (db.name && !KNOWN_INDEXED_DB_NAMES.includes(db.name as typeof KNOWN_INDEXED_DB_NAMES[number])) {
              return deleteIndexedDBByName(db.name);
            }
            return Promise.resolve();
          })
        );
      } catch (error) {
        console.warn('[Auth] indexedDB.databases() not supported or failed:', error);
      }
    }

    console.log('[Auth] IndexedDB cleared');
  } catch (error) {
    console.error('[Auth] Failed to clear IndexedDB:', error);
  }
};

/**
 * Clear all Cache Storage (Service Worker caches)
 */
const clearCacheStorage = async (): Promise<void> => {
  try {
    if (!('caches' in window)) return;

    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map((cacheName) => caches.delete(cacheName))
    );
    console.log('[Auth] Cache Storage cleared');
  } catch (error) {
    console.error('[Auth] Failed to clear Cache Storage:', error);
  }
};

/**
 * Unregister all Service Workers
 */
const unregisterServiceWorkers = async (): Promise<void> => {
  try {
    if (!('serviceWorker' in navigator)) return;

    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map((registration) => registration.unregister())
    );
    console.log('[Auth] Service Workers unregistered');
  } catch (error) {
    console.error('[Auth] Failed to unregister Service Workers:', error);
  }
};

const AUTH_KEYS = [
  'persist:auth',
  'resetEmail',
  STORAGE_KEYS.AUTH_TOKEN,
  STORAGE_KEYS.REFRESH_TOKEN,
  STORAGE_KEYS.USER_DATA,
  'auth_user',
  'loginResponse',
] as const;

const AUTH_COOKIES = ['auth_token', 'refresh_token', 'session_id'] as const;
const OAUTH_COOKIES = [
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'id_token',
  'oauth_state',
  'oauth_nonce',
  'session_id',
  'JSESSIONID',
] as const;

export class CrossTabLogout {
  private static instance: CrossTabLogout | null = null;
  private channel: BroadcastChannel | null = null;
  private onLogout?: () => void;
  private storageHandler?: (e: StorageEvent) => void;
  private constructor() {
    this.initBroadcastChannel();
    this.initStorageFallback();
  }
  private initBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel('auth_logout');
      this.channel.onmessage = (event) => {
        if (event.data?.type === 'LOGOUT') {
          this.onLogout?.();
        }
      };
    } catch {
    }
  }

  private initStorageFallback(): void {
    this.storageHandler = (event: StorageEvent) => {
      if (event.key === 'logout_signal' && event.newValue === 'true') {
        this.onLogout?.();
        localStorage.removeItem('logout_signal');
      }
    };
    window.addEventListener('storage', this.storageHandler);
  }

  static getInstance(): CrossTabLogout {
    if (!CrossTabLogout.instance) {
      CrossTabLogout.instance = new CrossTabLogout();
    }
    return CrossTabLogout.instance;
  }

  setLogoutCallback(callback: () => void): void {
    this.onLogout = callback;
  }

  broadcastLogout(): void {
    try {
      this.channel?.postMessage({ type: 'LOGOUT' });
      localStorage.setItem('logout_signal', 'true');
    } catch (error) {
      console.error('[Auth] Failed to broadcast logout:', error);
    }
  }

  destroy(): void {
    this.channel?.close();
    if (this.storageHandler) {
      window.removeEventListener('storage', this.storageHandler);
    }
    CrossTabLogout.instance = null;
  }
}

const clearCookie = (name: string): void => {
  const domain = window.location.hostname;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`;
};

export const clearOAuthCookies = (): void => {
  const domain = window.location.hostname;
  const paths = ['/', '/auth', '/auth/callback'];
  OAUTH_COOKIES.forEach((cookieName) => {
    paths.forEach((path) => {
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
      document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain}`;
      if (domain.includes('.')) {
        const parentDomain = domain.split('.').slice(-2).join('.');
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=.${parentDomain}`;
      }
    });
  });
};

export const clearAllStorage = (): void => {
  try {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (name) clearCookie(name);
    });
  } catch (error) {
    console.error('[Auth] Failed to clear storage:', error);
  }
};

/**
 * Clear ALL site data including IndexedDB, Cache Storage, Service Workers
 * This is the comprehensive cleanup function for logout
 */
export const clearAllSiteData = async (): Promise<void> => {
  try {
    // Clear synchronous storage first
    localStorage.clear();
    sessionStorage.clear();

    // Clear all cookies
    document.cookie.split(';').forEach((cookie) => {
      const name = cookie.split('=')[0].trim();
      if (name) clearCookie(name);
    });

    // Clear OAuth cookies
    clearOAuthCookies();

    // Clear async storage in parallel
    await Promise.all([
      clearIndexedDB(),
      clearCacheStorage(),
      unregisterServiceWorkers(),
    ]);

    console.log('[Auth] All site data cleared successfully');
  } catch (error) {
    console.error('[Auth] Failed to clear all site data:', error);
  }
};

export const clearAuthStorage = (): void => {
  try {
    AUTH_KEYS.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    AUTH_COOKIES.forEach(clearCookie);
  } catch (error) {
    console.error('[Auth] Failed to clear auth storage:', error);
  }
};

export const isAuthenticated = (): boolean => {
  try {
    return !!localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch {
    return false;
  }
};

export const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch {
    return null;
  }
};

export const setAuthToken = (token: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  } catch (error) {
    console.error('[Auth] Failed to set token:', error);
  }
};

export const removeAuthToken = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  } catch (error) {
    console.error('[Auth] Failed to remove token:', error);
  }
};

export type LogoutReason = 'manual' | 'timeout' | 'expired' | 'cross-tab' | 'error';
export interface LogoutOptions {
  showNotification?: boolean;
  reason?: LogoutReason;
  skipApi?: boolean;
}
let logoutInProgress = false;
let crossTabNotificationShown = false;
let inProgressResetTimer: ReturnType<typeof setTimeout> | null = null;

const AUTO_RESET_TIMEOUT_MS = 10_000;

export const LogoutService = {
  isInProgress(): boolean {
    return logoutInProgress;
  },
  setInProgress(value: boolean): void {
    logoutInProgress = value;

    // Clear any existing auto-reset timer
    if (inProgressResetTimer) {
      clearTimeout(inProgressResetTimer);
      inProgressResetTimer = null;
    }

    // When setting to true, start an auto-reset timer to prevent permanent stuck state
    if (value) {
      inProgressResetTimer = setTimeout(() => {
        if (logoutInProgress) {
          console.warn('[LogoutService] Auto-resetting stuck inProgress flag after timeout');
          logoutInProgress = false;
        }
        inProgressResetTimer = null;
      }, AUTO_RESET_TIMEOUT_MS);
    }
  },
  isCrossTabNotificationShown(): boolean {
    return crossTabNotificationShown;
  },
  setCrossTabNotificationShown(value: boolean): void {
    crossTabNotificationShown = value;
  },
  /**
   * Perform comprehensive cleanup of all site data
   * Clears: localStorage, sessionStorage, cookies, IndexedDB, Cache Storage, Service Workers
   */
  async performCleanup(): Promise<void> {
    try {
      // Broadcast logout to other tabs first
      try {
        const crossTabLogout = CrossTabLogout.getInstance();
        crossTabLogout.broadcastLogout();
      } catch (e) {
        console.warn('[LogoutService] Cross-tab broadcast failed:', e);
      }

      // Clear all site data (async)
      await clearAllSiteData();

      console.log('[LogoutService] Cleanup completed');
    } catch (error) {
      console.error('[LogoutService] Cleanup error:', error);
    }
  },
  /**
   * Perform local cleanup without broadcasting to other tabs
   */
  async performLocalCleanup(): Promise<void> {
    try {
      await clearAllSiteData();
    } catch (error) {
      console.error('[LogoutService] Local cleanup error:', error);
    }
  },
  resetCrossTabNotificationAfterDelay(delayMs: number = 5000): void {
    setTimeout(() => {
      crossTabNotificationShown = false;
    }, delayMs);
  },
  reset(): void {
    logoutInProgress = false;
    crossTabNotificationShown = false;
  },
};

export default {
  CrossTabLogout,
  clearAllStorage,
  clearAllSiteData,
  clearAuthStorage,
  clearOAuthCookies,
  isAuthenticated,
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  LogoutService,
};
